import React from 'react';
import {Grid} from '@mui/material';
import {Neighbourhood, NewJoinerType} from '../../api/Model';
import {TextSearchFilter} from '../../components/filter-fields/TextSearchFilter';
import {DateRangeFilter, DateRangeOption} from '../../components/filter-fields/DateRangeFilter';
import {StringEnumFilter} from '../../components/filter-fields/StringEnumFilter';
import {SetState} from '../../util/FormHandlers';
import {NeighbourhoodsFilter} from '../../components/filter-fields/NeighbourhoodsFilter';
import {today} from "../../util/DateUtil";
import {ResolvedNewJoiner} from "../../api/ResolvedNewJoinerStore";
import {filterSerializer} from "../../util/FilterSerializer";
import {ClearFilterButton} from "../../components/filter-fields/ClearFilterButton";
import {
    andFilter,
    applyFilter,
    filterChoice,
    filterDateRange,
    filterNeighbourhoods,
    filterText,
    filterValue,
    matchIfSelected
} from "../../util/FilterUtil";

export type NewJoinerStatus = 'all' | 'not-yet-joined' | 'already-joined';

export interface NewJoinersFilterForm {
    search: string;
    joinDateRange: DateRangeOption | undefined;
    neighbourhoods: Neighbourhood[];
    type: NewJoinerType | '';
    status: NewJoinerStatus;
}

export const emptyNewJoinersFilter: NewJoinersFilterForm = {
    search: '',
    joinDateRange: undefined,
    neighbourhoods: [],
    type: '',
    status: 'all',
};

export const defaultNewJoinersFilter: NewJoinersFilterForm = {
    ...emptyNewJoinersFilter,
    status: 'not-yet-joined',
};

export const newJoinersFilterSerializer = (allNeighbourhoods: Neighbourhood[]) => filterSerializer(defaultNewJoinersFilter, b => {
    b.str('search');
    b.dateRange('joinDateRange');
    b.neighbourhoods('neighbourhoods', allNeighbourhoods);
    b.str('type');
    b.str('status');
});

export function applyNewJoinersFilter(newJoiners: ResolvedNewJoiner[], filter: NewJoinersFilterForm): ResolvedNewJoiner[] {
    return applyFilter(newJoiners, andFilter<ResolvedNewJoiner>(
        filterText(filter.search, newJoiner => [
            newJoiner.person.type === 'existing'
                ? newJoiner.person.employee.fullName
                : newJoiner.person.fullName,
            newJoiner.person.type === 'new' ?
                newJoiner.person.manager?.fullName
                : undefined,
        ]),
        filterValue(
            filterDateRange(filter.joinDateRange),
            newJoiner => newJoiner.joinDate,
        ),
        filterNeighbourhoods(
            filter.neighbourhoods,
            newJoiner => newJoiner.employee?.neighbourhoods
        ),
        filterValue(
            matchIfSelected(filter.type, ''),
            newJoiner => newJoiner.type,
        ),
        filterChoice(filter.status, {
            all: undefined,
            'not-yet-joined': newJoiner => newJoiner.joinDate > today(),
            'already-joined': newJoiner => newJoiner.joinDate <= today(),
        }),
    ));
}

const NEW_JOINER_TYPE_OPTIONS = [
    {value: '' as NewJoinerType | '', label: 'All Types'},
    {value: NewJoinerType.InternationalRelocation, label: 'International Relocation'},
    {value: NewJoinerType.LocalRelocation, label: 'Local Relocation'},
    {value: NewJoinerType.NewFTE, label: 'New FTE'},
    {value: NewJoinerType.NewIntern, label: 'New Intern'},
    {value: NewJoinerType.InternToFTE, label: 'Intern to FTE'},
    {value: NewJoinerType.ReturnFromParentalLeave, label: 'From Parental Leave'},
];

const STATUS_OPTIONS: { value: NewJoinerStatus, label: string }[] = [
    {value: 'all', label: 'All'},
    {value: 'not-yet-joined', label: 'Not Yet Joined'},
    {value: 'already-joined', label: 'Already Joined'},
];

export function NewJoinersFilter({filter, setFilter}: {
    filter: NewJoinersFilterForm;
    setFilter: SetState<NewJoinersFilterForm>;
}) {
    return (
        <Grid container spacing={2} alignItems="center">
            <DateRangeFilter
                filter={filter}
                setFilter={setFilter}
                property="joinDateRange"
                label="Join date"
            />
            <StringEnumFilter
                label="Type"
                filter={filter}
                setFilter={setFilter}
                property="type"
                options={NEW_JOINER_TYPE_OPTIONS}
            />
            <StringEnumFilter
                label="Status"
                filter={filter}
                setFilter={setFilter}
                property="status"
                options={STATUS_OPTIONS}
            />
            <NeighbourhoodsFilter filter={filter} setFilter={setFilter}/>
            <TextSearchFilter filter={filter} setFilter={setFilter} property="search"/>
            <ClearFilterButton emptyFilter={emptyNewJoinersFilter} setFilter={setFilter}/>
        </Grid>
    );
}
